import { useState } from "react";
import {
  DEFAULT_OPTIONS,
  planBody,
  saveBody,
  useAcceptPlan,
  usePlanRoutes,
  usePlannerCount,
  useSavePlan,
  type PlanOptions,
  type LeftOut,
  type PlannerContext,
  type Point,
  type Proposal,
} from "../../api/routing";
import type { SiteFilterQuery } from "../../api/sites";
import { useDebounced } from "../../hooks/useDebounced";
import DayStep from "./DayStep";
import GatedNote from "./GatedNote";
import PlanBar from "./PlanBar";
import PlannerMap from "./PlannerMap";
import { daySummary, sitesSummary, startSummary } from "./plannerSummary";
import ProposalDays from "./ProposalDays";
import SitesStep from "./SitesStep";
import StartStep, { type Picking } from "./StartStep";
import StepCard from "./StepCard";
import { useDayCrews } from "./useDayCrews";

type Step = "sites" | "start" | "day";

/** The planner: three steps on the left (which sites, where the day starts,
 * the day), the map on the right, then the proposed days with a crew picker
 * each and a bar to save and accept. The map and the day cards are drawn from
 * one proposal, so they cannot disagree. */
/** What the route leaves out by default -- sites still to be visited on a
 * live route plan, and sites already visited -- and the switch that routes
 * them again on purpose. A site whose last visit was not done is never left
 * out: it is owed one. */
function LeftOutNote({ leftOut, include, onInclude }: { leftOut: LeftOut | undefined; include: boolean; onInclude: (next: boolean) => void }) {
  const total = (leftOut?.planned ?? 0) + (leftOut?.visited ?? 0);
  const parts = [
    leftOut && leftOut.planned > 0 ? `${leftOut.planned} already on a route plan${leftOut.plans.length > 0 ? ` (${leftOut.plans.join(", ")})` : ""}` : null,
    leftOut && leftOut.visited > 0 ? `${leftOut.visited} visited` : null,
  ].filter(Boolean);
  return (
    <div className="planner-left-out">
      {!include && total > 0 ? <p className="notice warn">{`${total} left out: ${parts.join(", ")}.`}</p> : null}
      <label className="check">
        <input type="checkbox" checked={include} onChange={(e) => onInclude(e.target.checked)} /> Include sites already planned or visited
      </label>
    </div>
  );
}

export default function Planner({ context, initialFilter }: { context: PlannerContext; initialFilter: SiteFilterQuery }) {
  const contracts = context.filters.contracts;
  const [filter, setFilter] = useState<SiteFilterQuery>(initialFilter);
  const [siteIds, setSiteIds] = useState<number[]>(context.selection.site_ids);
  const [includePlanned, setIncludePlanned] = useState(false);
  const [origin, setOrigin] = useState<Point | null>(
    context.start.lat !== null && context.start.lon !== null ? { lat: context.start.lat, lon: context.start.lon, label: context.start.label } : null,
  );
  const [meeting, setMeeting] = useState<Point | null>(null);
  const [options, setOptions] = useState<PlanOptions>(DEFAULT_OPTIONS);
  const [open, setOpen] = useState<Step | null>("sites");
  const [picking, setPicking] = useState<Picking>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [planName, setPlanName] = useState("");
  const [contractId, setContractId] = useState(filter.contract_id || (contracts.length === 1 ? String(contracts[0].id) : ""));
  const plan = usePlanRoutes();
  const save = useSavePlan();
  const accept = useAcceptPlan();
  const count = usePlannerCount(useDebounced(filter, 300), siteIds, includePlanned);
  const defaultProvider =
    context.default_provider_id !== null && context.filters.providers.some((p) => p.id === context.default_provider_id) ? String(context.default_provider_id) : "";
  const crews = useDayCrews(proposal, savedId, defaultProvider);

  function toggle(step: Step) {
    setOpen((current) => (current === step ? null : step));
  }
  function changeFilter(next: SiteFilterQuery) {
    if (next.contract_id !== filter.contract_id && next.contract_id !== "") {
      setContractId(next.contract_id);
    }
    setFilter(next);
  }
  function runPlan() {
    if (origin === null) {
      return;
    }
    plan.mutate(planBody(origin, meeting, siteIds, filter, options, includePlanned), {
      onSuccess: (p) => {
        setProposal(p);
        setSavedId(null);
        setOpen(null);
        save.reset();
        accept.reset();
      },
    });
  }
  function savePlan() {
    if (proposal === null || contractId === "") {
      return;
    }
    save.mutate(saveBody(proposal, Number(contractId), planName, filter, options), {
      onSuccess: (r) => {
        setSavedId(r.plan_id);
        void crews.sendAll(r.plan_id);
      },
    });
  }

  return (
    <div className="planner">
      <aside className="planner-steps" aria-label="Plan the routes">
        <StepCard n={1} title="Which sites" summary={sitesSummary(filter, context, count.data)} open={open === "sites"} onToggle={() => toggle("sites")}>
          <SitesStep context={context} filter={filter} onFilter={changeFilter} siteIds={siteIds} onSiteIds={setSiteIds} />
          <LeftOutNote leftOut={count.data?.left_out} include={includePlanned} onInclude={setIncludePlanned} />
          <GatedNote sites={count.data?.gated?.sites} total={count.data?.gated?.count} />
          {count.isError ? <p className="notice bad">The sites could not be counted: {count.error.message}</p> : null}
        </StepCard>
        <StepCard n={2} title="Where the day starts" summary={startSummary(origin, meeting)} open={open === "start"} onToggle={() => toggle("start")}>
          <StartStep
            context={context}
            contractId={contractId}
            origin={origin}
            meeting={meeting}
            onOrigin={setOrigin}
            onMeeting={setMeeting}
            picking={picking}
            onPicking={setPicking}
          />
        </StepCard>
        <StepCard n={3} title="The day" summary={daySummary(options)} open={open === "day"} onToggle={() => toggle("day")}>
          <DayStep options={options} onOptions={setOptions} />
        </StepCard>
        <button type="button" className="btn btn-primary btn-big btn-block" disabled={plan.isPending || origin === null} onClick={runPlan}>
          {plan.isPending ? "Planning…" : proposal ? "Plan again" : "Plan the routes"}
        </button>
        {origin === null ? <p className="planner-hint">Set a depot in step 2 first.</p> : null}
        {plan.isError ? <p className="notice bad">{plan.error.message}</p> : null}
      </aside>

      <section className="planner-main" aria-label="Route map and days">
        <PlannerMap
          origin={origin}
          meeting={meeting}
          proposal={proposal}
          picking={picking}
          onPicked={(point) => {
            if (picking === "meeting") {
              setMeeting(point);
            } else {
              setOrigin(point);
            }
            setPicking(null);
          }}
        />
        {proposal === null ? (
          <p className="planner-empty">
            Choose the sites, set the depot and press <strong>Plan the routes</strong>.
          </p>
        ) : (
          <>
            <p className={proposal.approximate ? "notice warn" : "notice ok"}>
              {proposal.approximate
                ? "Straight-line estimate: the routing service was unavailable, so distances are approximate and the lines are dashed."
                : `Planned across ${proposal.days.length} day${proposal.days.length === 1 ? "" : "s"} on real road routing.`}
              {proposal.skipped_site_ids.length > 0 ? ` ${proposal.skipped_site_ids.length} site(s) without coordinates were left out.` : ""}
            </p>
            <GatedNote sites={proposal.gated} />
            {save.isError ? <p className="notice bad">The plan could not be saved: {save.error.message}</p> : null}
            {accept.isError ? <p className="notice bad">The plan could not be accepted: {accept.error.message}</p> : null}
            <ProposalDays proposal={proposal} providers={context.filters.providers} crews={crews} locked={accept.isSuccess} />
            <PlanBar
              proposal={proposal}
              context={context}
              crews={crews}
              contractId={contractId}
              onContractId={setContractId}
              planName={planName}
              onPlanName={setPlanName}
              savedId={savedId}
              saving={save.isPending}
              onSave={savePlan}
              accepting={accept.isPending}
              accepted={accept.data ?? null}
              onAccept={() => savedId !== null && accept.mutate(savedId)}
            />
          </>
        )}
      </section>
    </div>
  );
}
